import React, { useState, useEffect } from "react";
import {
	Button,
	Card,
	Col,
	CardBody,
	CardHeader,
	CardTitle,
	Row,
	Form,
} from "reactstrap";
import formNavigationUtils from "utils/formNavigationUtils";
import { showApiError } from "../../../../../utils/errorRoutingUtils";
import {
	CheckBoxElement,
	CurrencyInput,
	IntegerInput,
	SelectorElement,
} from "../../../../forms/components/InputElement";
import {
	saveFailAlert,
	saveSuccessAlert,
	retrieveFailAlert,
} from "../../../../../utils/alertUtils";
import {
	getLeaseDefaults,
	saveLeaseDefaults,
} from "../../../../../api/DealAPI";
import { useFromUser } from "../../../../../stores/LocalStorageHelper";
import useCurrentLeaseStore from "../../../../../stores/LeaseStore";
import shallow from "zustand/shallow";

// Safe number coercion
const n = (v, fallback = 0) => {
	const num = typeof v === "string" ? parseFloat(v) : Number(v);
	return Number.isFinite(num) ? num : fallback;
};

const LeaseSettings = ({ isDeal }) => {
	console.log(isDeal);
	const locationID = useFromUser("locationID");
	const dealerID = useFromUser("dealerID");

	// Disposition fee options
	const dispositionOptions = [
		{ label: "At Lease End", value: 0 },
		{ label: "Drive-Off", value: 1 },
		{ label: "Capitalized", value: 2 },
	];

	// --- boolean toggles ---
	const [capBankFee, setCapBankFee] = useState(false);
	const [capDMVFee, setCapDMVFee] = useState(false);
	const [capDocFee, setCapDocFee] = useState(false);
	const [capSalesTax, setCapSalesTax] = useState(false);
	const [capMiscFee, setCapMiscFee] = useState(false);
	const [capOtherfee, setCapOtherfee] = useState(false);
	const [capTaxOnReduction, setCapTaxOnReduction] = useState(false);
	const [capFirstPmt, setCapFirstPmt] = useState(false);
	const [capWarranty, setCapWarranty] = useState(false);
	const [capGap, setCapGap] = useState(false);
	const [capDispositionFee, setCapDispositionFee] = useState(0); // 0=At Lease End, 1=Drive-Off, 2=Capitalized

	// --- numeric fields ---
	const [moneyFactor, setMoneyFactor] = useState(0);
	const [allowableMilesPerYear, setAllowableMilesPerYear] = useState(0);
	const [capReduction, setCapReduction] = useState(0);
	const [bankFee, setBankFee] = useState(0);
	const [capSmogFees, setCapSmogFees] = useState(false);
	const [additionalFee, setAdditionalFee] = useState(0);
	const [terminationFee, setTerminationFee] = useState(false);
	const [dispositionFee, setDispositionFee] = useState(false);
	const [proRataRent, setProRataRent] = useState(0);
	const [proRataSalesTax, setProRataSalesTax] = useState(0);
	const [securityDeposit, setSecurityDeposit] = useState(0);
	const [id, setId] = useState(null);
	const lease = useCurrentLeaseStore((state) => ({ ...state }), shallow);
	// Load defaults (inline effect ⇒ no exhaustive-deps warning)
	useEffect(() => {
		if (isDeal) {
			setCapBankFee(lease.capBankFee);
			setCapDMVFee(lease.capDMVFee);
			setCapDocFee(lease.capDocFee);
			setCapSalesTax(lease.capSalesTax);
			setCapMiscFee(lease.capMiscFee);
			setCapOtherfee(lease.capOtherfee);
			setCapTaxOnReduction(lease.capTaxOnReduction);
			setCapFirstPmt(lease.capFirstPmt);
			setCapWarranty(lease.capWarranty);
			setCapGap(lease.capGAPWaiver);
			setCapSmogFees(lease.capSmogFees);
			setCapDispositionFee(n(lease.capDispositionFee, 0));

			setMoneyFactor(n(lease.moneyFactor, 0));
			setAllowableMilesPerYear(n(lease.allowableMilesPerYear, 0));
			setCapReduction(n(lease.capReduction, 0));
			setBankFee(n(lease.bankFee, 0));
			setAdditionalFee(n(lease.additionalFee, 0));
			setTerminationFee(lease.terminationFee, 0);
			setDispositionFee(lease.disposition, 0);
			setProRataRent(n(lease.proRataRent, 0));
			setProRataSalesTax(n(lease.proRataSalesTax, 0));
			setSecurityDeposit(n(lease.securityDeposit, 0));
		} else {
			getLeaseDefaults(locationID).then(
				(res) => {
					console.log(res);
					const d = res?.data?.content || {};
					setId(d.id);
					setCapBankFee(Boolean(d.capBankFee));
					setCapDMVFee(Boolean(d.capDMVFee));
					setCapDocFee(Boolean(d.capDocFee));
					setCapSalesTax(Boolean(d.capSalesTax));
					setCapMiscFee(Boolean(d.capMiscFee));
					setCapOtherfee(Boolean(d.capOtherFee));
					setCapTaxOnReduction(Boolean(d.capTaxOnReduction));
					setCapFirstPmt(Boolean(d.capFirstPmt));
					setCapWarranty(Boolean(d.capWarranty));
					setCapGap(Boolean(d.capGAPWaiver));
					setCapSmogFees(Boolean(d.capSmogFees));
					setCapDispositionFee(n(d.capDispositionFee, 0));

					setMoneyFactor(n(d.moneyFactor, 0));
					//setAllowableMilesPerYear(n(d.allowableMilesPerYear, 0));
					//setCapReduction(n(d.capReduction, 0));
					//setBankFee(n(d.bankFee, 0));
					setTerminationFee(n(d.terminationFee, 0));
					setDispositionFee(n(d.dispositionFee, 0));
					//setProRataRent(n(d.proRataRent, 0));
					//setProRataSalesTax(n(d.proRataSalesTax, 0));
				},
				(err) => {
					console.log(err);
					// if (!err?.isGeneralError) {
					// 	showApiError(err, retrieveFailAlert);
					// }
				}
			);
		}
	}, []);

	const save = () => {
		console.log("Saving Lease Settings");
		const payload = {
			id: id,
			capBankFee: Boolean(capBankFee),
			capDMVFee: Boolean(capDMVFee),
			capDocFee: Boolean(capDocFee),
			capSalesTax: Boolean(capSalesTax),
			capMiscFee: Boolean(capMiscFee),
			capOtherFee: Boolean(capOtherfee),
			capTaxOnReduction: Boolean(capTaxOnReduction),
			capFirstPmt: Boolean(capFirstPmt),
			capWarranty: Boolean(capWarranty),
			capGAPWaiver: Boolean(capGap),
			capSmogFee: Boolean(capSmogFees),
			capDispositionFee: n(capDispositionFee, 0),
			dispositionFee: n(dispositionFee, 0),
			moneyFactor: n(moneyFactor, 0),
			additionalFee: n(additionalFee, 0),
			capReduction: n(capReduction, 0),
			terminationFee: n(terminationFee, 0),
			locationID: locationID,
			dealerID: dealerID,
		};

		saveLeaseDefaults(payload).then(
			() => {
				console.log("saveLeaseDefaults", payload);
				saveSuccessAlert();
			},
			(err) => {
				if (!err?.isGeneralError) {
					showApiError(err, saveFailAlert);
				}
			}
		);
	};

	return (
		<div onKeyDown={formNavigationUtils}>
			<Form>
				<Card>
					<CardHeader>
						<CardTitle tag="h3">Lease/RTO Settings</CardTitle>
					</CardHeader>
					<Row className="m-0">
						<Col>
							<CardBody>
								<CheckBoxElement
									disableValidation
									checked={capBankFee}
									onChange={() => {
										if (isDeal) lease.editField("capBankFee", !capBankFee);
										setCapBankFee(!capBankFee);
									}}
									name="capBankFee"
									label="Bank Fee to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capDocFee}
									onChange={() => {
										if (isDeal) lease.editField("capDocFee", !capDocFee);
										setCapDocFee(!capDocFee);
									}}
									name="capDocFee"
									label="Doc Fee to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capMiscFee}
									onChange={() => {
										if (isDeal) lease.editField("capMiscFee", !capMiscFee);
										setCapMiscFee(!capMiscFee);
									}}
									name="capMiscFee"
									label="Misc Fee to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capOtherfee}
									onChange={() => {
										if (isDeal) lease.editField("capOtherfee", !capOtherfee);
										setCapOtherfee(!capOtherfee);
									}}
									name="capOtherfee"
									label="Other Fee to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capDMVFee}
									onChange={() => {
										if (isDeal) lease.editField("capDMVFee", !capDMVFee);
										setCapDMVFee(!capDMVFee);
									}}
									name="capDMVFee"
									label="DMV Fees to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>
								<CheckBoxElement
									disableValidation
									checked={capSmogFees}
									onChange={() => {
										if (isDeal) lease.editField("capSmogFees", !capSmogFees);
										setCapSmogFees(!capSmogFees);
									}}
									name="capSmogFees"
									label="Smog Fees to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>
							</CardBody>
						</Col>

						<Col>
							<CardBody>
								<CheckBoxElement
									disableValidation
									checked={capGap}
									onChange={() => {
										if (isDeal) lease.editField("capGap", !capGap);
										setCapGap(!capGap);
									}}
									name="capGap"
									label="GAP to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capWarranty}
									onChange={() => {
										if (isDeal) lease.editField("capWarranty", !capWarranty);
										setCapWarranty(!capWarranty);
									}}
									name="capWarranty"
									label="Warranty to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capSalesTax}
									onChange={() => {
										if (isDeal) lease.editField("capSalesTax", !capSalesTax);
										setCapSalesTax(!capSalesTax);
									}}
									name="capSalesTax"
									label="Sales Tax to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									disabled={!capSalesTax}
									checked={capTaxOnReduction}
									onChange={() => {
										if (isDeal)
											lease.editField("capTaxOnReduction", !capTaxOnReduction);
										setCapTaxOnReduction(!capTaxOnReduction);
									}}
									name="capTaxOnReduction"
									label="Cap Tax on Reduction"
									colSize="col-sm-3"
									labelClass="pl-3 "
									bordered={false}
								/>

								<CheckBoxElement
									disableValidation
									checked={capFirstPmt}
									onChange={() => {
										if (isDeal) lease.editField("capFirstPmt", !capFirstPmt);
										setCapFirstPmt(!capFirstPmt);
									}}
									name="capFirstPmt"
									label="First Payment to Cap Cost"
									colSize="col-sm-3"
									labelClass="pl-3"
									bordered={false}
								/>
							</CardBody>
						</Col>

						<Col>
							<CardBody>
								<CurrencyInput
									disableValidation
									value={dispositionFee}
									onChange={(e) => {
										if (isDeal) lease.editField("disposition", e);
										setDispositionFee(e);
									}}
									name="dispositionFee"
									label="Disposition Fee"
									type="number"
									colSize="col-sm-12"
								/>
								<SelectorElement
									disableValidation
									value={capDispositionFee}
									onChange={(e) => {
										const value = Number(e.target.value);
										if (isDeal) lease.editField("capDispositionFee", value);
										setCapDispositionFee(value);
									}}
									name="capDispositionFee"
									label="Disposition Fee Placement"
									options={dispositionOptions}
									colSize="col-sm-12"
								/>

								{!isDeal && (
									<IntegerInput
										disableValidation
										value={moneyFactor}
										onChange={(e) => {
											if (isDeal) {
												console.log("moneyFactor", e);
												lease.editField("moneyFactor", e);
											}
											setMoneyFactor(e);
										}}
										name="moneyFactor"
										label="Money Factor"
										type="number"
										colSize="col-sm-12"
										decimalScale={8}
									/>
								)}
								<IntegerInput
									disableValidation
									value={allowableMilesPerYear}
									onChange={(e) => {
										if (isDeal)
											lease.editField(
												"allowableMilesPerYear",
												e?.target?.value
											);
										setAllowableMilesPerYear(e?.target?.value);
									}}
									name="allowableMilesPerYear"
									label="Allowable Miles Per Year"
									type="number"
									colSize="col-sm-12"
								/>
								<CurrencyInput
									disableValidation
									value={additionalFee}
									onChange={(e) => {
										if (isDeal) lease.editField("additionalFee", e);
										setAdditionalFee(e);
									}}
									name="additionalFee"
									label="Periodic Fee"
									type="number"
									colSize="col-sm-12"
								/>
								<CurrencyInput
									disableValidation
									value={terminationFee}
									onChange={(e) => {
										if (isDeal) lease.editField("terminationFee", e);
										setTerminationFee(e);
									}}
									name="terminationFee"
									label="Termination Fee"
									type="number"
									colSize="col-sm-12"
								/>
								{!isDeal && (
									<div className="btn btn-success float-button" onClick={save}>
										<i className="nc-icon nc-cloud-download-93"></i> Save
										Default
									</div>
								)}
							</CardBody>
						</Col>

						{isDeal && (
							<Col>
								<CardBody>
									<CurrencyInput
										disableValidation
										value={proRataRent}
										onChange={(e) => {
											lease.editField("proRataRent", e);
											setProRataRent(e);
										}}
										name="proRataRent"
										label="Pro-Rata Rent"
										type="number"
										colSize="col-sm-12"
									/>
									<CurrencyInput
										disableValidation
										value={proRataSalesTax}
										onChange={(e) => {
											lease.editField("proRataSalesTax", e);
											setProRataSalesTax(e);
										}}
										name="proRataSalesTax"
										label="Pro-Rata SalesTax"
										type="number"
										colSize="col-sm-12"
									/>
									<CurrencyInput
										disableValidation
										value={securityDeposit}
										onChange={(e) => {
											lease.editField("securityDeposit", e);
											setSecurityDeposit(e);
										}}
										name="securityDeposit"
										label="Security Deposit"
										type="number"
										colSize="col-sm-12"
									/>
								</CardBody>
							</Col>
						)}
					</Row>
				</Card>
			</Form>
		</div>
	);
};

export default LeaseSettings;
